import { Link } from "react-router-dom";
import { Plus, FileText } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PaperCard } from "@/components/ui/paper-card";
import { EmptyState } from "@/components/empty-state";
import { EntityErrorState } from "@/components/layout/entity-state";
import { CanEdit, useEditorCopy } from "@/lib/project-access";
import { WalkthroughPersonaHeader } from "@/components/experience/walkthrough-persona-header";
import { WalkthroughVerdictBlock } from "@/components/experience/walkthrough-verdict-block";
import {
  ReasoningText,
  findingTitleMap,
} from "@/components/walkthrough/reasoning-text";
import {
  getPrimaryVerdict,
  type VerdictRating,
} from "@/components/experience/vocabulary";
import { DashboardSectionHeader } from "./dashboard-section-header";
import { LatestFindings } from "./latest-findings";
import {
  ActiveWalkthroughHeroCard,
  FailedWalkthroughHeroCard,
} from "./walkthrough-hero-status-cards";
import type { WalkthroughListItem } from "./dashboard-types";

interface LatestWalkthroughHeroProps {
  projectId: string;
  walkthroughs: WalkthroughListItem[] | undefined;
  scenarioCount?: number;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

export function LatestWalkthroughHero({
  projectId,
  walkthroughs,
  scenarioCount = 0,
  isLoading,
  isError,
  onRetry,
}: LatestWalkthroughHeroProps) {
  const latest = walkthroughs?.[0];
  const lastCompleted = walkthroughs?.find((w) => w.status === "completed");
  const latestCompletedId =
    latest?.status === "completed" ? latest.id : lastCompleted?.id;

  const emptyDescriptionWithScenarios = useEditorCopy(
    `"Would this person come back and use this voluntarily?" — run your first walkthrough across your ${scenarioCount} ${scenarioCount === 1 ? "scenario" : "scenarios"} to find out.`,
    "No one has run a walkthrough in this project yet. Ask an editor to run one.",
  );
  const emptyDescriptionWithoutScenarios = useEditorCopy(
    "Create your first scenario to test user journeys and identify usability issues.",
    "No scenarios have been created in this project yet. Ask an editor to create one.",
  );

  const { data: latestFindings } = trpc.finding.list.useQuery(
    { projectId, walkthroughId: latestCompletedId ?? "" },
    { enabled: !!latestCompletedId },
  );

  if (isLoading) {
    return (
      <section>
        <DashboardSectionHeader kicker="Latest walkthrough" />
        <Skeleton className="h-64 w-full" />
      </section>
    );
  }

  if (isError) {
    return (
      <section>
        <DashboardSectionHeader kicker="Latest walkthrough" />
        <PaperCard as="article" padding="md">
          <EntityErrorState
            title="Failed to load walkthroughs"
            description="We encountered an issue loading the walkthroughs for this project."
            onRetry={onRetry}
            embedded
          />
        </PaperCard>
      </section>
    );
  }

  if (!latest) {
    return (
      <section>
        <DashboardSectionHeader kicker="Latest walkthrough" />
        <EmptyState
          icon={FileText}
          title={
            scenarioCount > 0
              ? "No walkthroughs run yet"
              : "No walkthroughs yet"
          }
          description={
            scenarioCount > 0
              ? emptyDescriptionWithScenarios
              : emptyDescriptionWithoutScenarios
          }
          actions={
            <CanEdit>
              {scenarioCount > 0 ? (
                <Button asChild variant="outline">
                  <Link to={`/projects/${projectId}/scenarios`}>
                    View scenarios ({scenarioCount}) →
                  </Link>
                </Button>
              ) : (
                <Button asChild>
                  <Link to={`/projects/${projectId}/scenarios`}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create a scenario
                  </Link>
                </Button>
              )}
            </CanEdit>
          }
        />
      </section>
    );
  }

  const scenarioTo =
    latest.outlineId && latest.personaId
      ? `/projects/${projectId}/scenarios/${latest.outlineId}#persona-${latest.personaId}`
      : latest.outlineId
        ? `/projects/${projectId}/scenarios/${latest.outlineId}`
        : null;

  if (latest.status === "running" || latest.status === "pending") {
    return (
      <ActiveWalkthroughHeroCard
        projectId={projectId}
        latest={latest}
        lastCompleted={lastCompleted}
        scenarioTo={scenarioTo}
      />
    );
  }

  if (latest.status === "failed" || latest.status === "timeout") {
    return (
      <FailedWalkthroughHeroCard
        projectId={projectId}
        latest={latest}
        lastCompleted={lastCompleted}
        scenarioTo={scenarioTo}
      />
    );
  }

  const primaryRating = getPrimaryVerdict(
    latest.verdict as Array<{ rating: VerdictRating }> | null,
  );
  const verdicts = Array.isArray(latest.verdict)
    ? (latest.verdict as Array<{ rating: VerdictRating; reasoning: string }>)
    : null;
  const primaryVerdict =
    verdicts?.find((v) => v.rating === primaryRating) ?? verdicts?.[0];
  const findingTitles = findingTitleMap(latestFindings ?? []);

  return (
    <section>
      <DashboardSectionHeader kicker="Latest walkthrough" />
      <PaperCard as="article" padding="md" className="space-y-4">
        <WalkthroughPersonaHeader
          personaName={latest.personaName}
          personaTo={
            latest.personaId
              ? `/projects/${projectId}/personas/${latest.personaId}`
              : null
          }
          scenarioName={latest.scenarioName}
          scenarioTo={scenarioTo}
          timestamp={latest.completedAt ?? latest.createdAt}
        />

        <WalkthroughVerdictBlock
          rating={primaryRating}
          reasoning={
            primaryVerdict?.reasoning ? (
              <ReasoningText
                text={primaryVerdict.reasoning}
                findings={findingTitles}
                projectId={projectId}
                walkthroughId={latest.id}
              />
            ) : null
          }
          status={latest.status}
          quoteLineClamp={3}
        />

        <LatestFindings projectId={projectId} walkthroughId={latest.id} />

        <div className="flex gap-2 border-t border-border/60 pt-4">
          <Button asChild variant="outline" size="sm">
            <Link to={`/projects/${projectId}/walkthroughs/${latest.id}`}>
              View full report
            </Link>
          </Button>
          <CanEdit>
            <Button asChild variant="ghost" size="sm">
              <Link
                to={
                  latest.outlineId && latest.personaId
                    ? `/projects/${projectId}/scenarios/${latest.outlineId}#persona-${latest.personaId}`
                    : latest.outlineId
                      ? `/projects/${projectId}/scenarios/${latest.outlineId}`
                      : `/projects/${projectId}/scenarios`
                }
              >
                View scenario
              </Link>
            </Button>
          </CanEdit>
        </div>
      </PaperCard>
    </section>
  );
}
